import type { Labels } from '../types/config';
import type { BillingDetails } from '../types/paymentRequest';
import type { VendorAccount } from '../types/paymentMethodSet';
import { PaymentMethodType } from '../types/paymentMethodSet';
import { Ids } from './ids';
import type { Theme, ThemeDesignTokens } from '../types/theme';
import { ThemeMode, ThemeType } from '../types/theme';
import type { Deferred } from '../types/utility';

export function isObject(v: any): boolean {
  return !!v && Object.getPrototypeOf(v) === Object.prototype;
}

export function isPromise(v: any): boolean {
  return Boolean(v && typeof v.then === 'function');
}

export function deepMerge(target: any, ...sources: any[]): any {
  if (isObject(target) && sources) {
    sources.filter(isObject).forEach((source: any) => {
      for (const key in source) {
        if (isObject(source[key])) {
          target[key] = deepMerge(target[key] || {}, source[key]);
        } else {
          target[key] = source[key];
        }
      }
    });
  }
  return target;
}

export function removeUndefinedProperties(object: { [k: string]: any }): { [k: string]: any } {
  if (!isObject(object)) {
    // Only remove undefined properties from objects
    return object;
  }
  const removed = { ...object };
  Object.keys(removed).forEach(function (name) {
    const value = removed[name];
    if (typeof value === 'undefined') {
      // Remove undefined property
      delete removed[name];
    } else if (isObject(value)) {
      // Recursively remove undefined properties in object property
      removed[name] = removeUndefinedProperties(removed[name]);
    }
  });
  return removed;
}

export function fallback(...values: any[]): any {
  const definedValues = values.filter((v) => v !== undefined && v !== null);
  return definedValues[0];
}

export function getEnhancedReturnUrl(
  url: string,
  account: VendorAccount,
  billingDetails: BillingDetails,
  uuid?: string
): string {
  const urlWithParams = new URL(url);
  urlWithParams.searchParams.append('accountId', account.accountId);
  urlWithParams.searchParams.append('gatewayId', account.gatewayId);
  urlWithParams.searchParams.append('key', account.config.key);
  urlWithParams.searchParams.append('vendor', account.vendor);
  if (uuid) {
    urlWithParams.searchParams.append('uuid', uuid);
  }
  urlWithParams.searchParams.append('billingName', billingDetails.name);
  urlWithParams.searchParams.append('billingEmail', billingDetails.email);
  urlWithParams.searchParams.append('billingPhone', billingDetails.phone);
  urlWithParams.searchParams.append('billingLine1', billingDetails.address.line1);
  urlWithParams.searchParams.append('billingLine2', billingDetails.address.line2);
  urlWithParams.searchParams.append('billingCity', billingDetails.address.city);
  urlWithParams.searchParams.append('billingState', billingDetails.address.state);
  urlWithParams.searchParams.append('billingCountry', billingDetails.address.country);
  urlWithParams.searchParams.append('billingPostalCode', billingDetails.address.postalCode);

  return urlWithParams.href;
}

export function createHeaderElement(iconUrl: string, _iconTitle: string, nameContent: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.innerHTML =
    `<span class="sfpp-payment-method-name">${nameContent}</span>` +
    `<img class="sfpp-payment-method-icon" src="${iconUrl}" alt="" />`;
  return el;
}

export function removeClass(element: Element, c: string): void {
  c.split(/(\s+)/)
    .filter((e) => e.trim().length > 0)
    .forEach((className) => {
      element.classList.remove(className);
    });
}

export function addClass(element: Element, c: string): void {
  c.split(/(\s+)/)
    .filter((e) => e.trim().length > 0)
    .forEach((className) => {
      if (!element.classList.contains(className)) {
        element.classList.add(className);
      }
    });
}

export function createFormElement(
  labelElement: HTMLElement,
  bodyElement: HTMLElement,
  errorElement: HTMLElement,
  className: string,
  required = false,
  tooltipElement?: HTMLElement
): HTMLElement {
  // Create form element control element
  const controlElement = document.createElement('div');
  controlElement.className = 'sfpp-form-element__control';
  controlElement.appendChild(bodyElement);

  // Create form element
  const formElement = document.createElement('div');
  formElement.className = 'sfpp-form-element ' + className;
  formElement.appendChild(labelElement);
  formElement.appendChild(controlElement);
  formElement.appendChild(errorElement);
  if (tooltipElement) {
    formElement.appendChild(tooltipElement);
  }

  // Add standard classes to elements in form element
  addClass(labelElement, 'sfpp-form-element__label ' + className + '-label');
  addClass(bodyElement, className + '-element');
  addClass(errorElement, 'sfpp-form-element__help ' + className + '-error');
  if (tooltipElement) {
    addClass(tooltipElement, className + '-tooltip');
  }

  // Link label and its input element
  bodyElement.id = Ids.nextId('sfpp-');
  labelElement.setAttribute('for', bodyElement.id);

  // Set id to use for aria-describedby when element has error
  errorElement.id = Ids.nextId('sfpp-');

  if (required) {
    // Add required field abbr element before text in label
    labelElement.innerHTML = '<abbr class="sfpp-required" title="required">*</abbr>' + labelElement.innerHTML;
  }

  return formElement;
}

export const DEFAULT_LABELS: Labels = {
  amountItemDescription: 'Item',
  afterpayClearpayRedirect: "To complete your payment, you'll be redirected to Afterpay.",
  afterpayClearpayIconTitle: 'Afterpay icon',
  afterpayClearpayTitle: 'Afterpay',
  bacsDebit: '',
  bacsDebitIconTitle: 'Bacs icon',
  bacsDebitTitle: 'Bacs',
  applePayRedirect: 'To complete your payment, select a card when prompted.',
  applePayIconTitle: 'Apple Pay icon',
  applePayTitle: 'Apple Pay',
  auBecsDebit: '',
  auBecsDebitIconTitle: 'BECS icon',
  auBecsDebitTitle: 'BECS Debit',
  googlePayRedirect: 'To complete your payment, select a card when prompted.',
  googlePayIconTitle: 'Google Pay icon',
  googlePayTitle: 'Google Pay',
  bancontactRedirect: "To complete your payment, you'll be redirected to Bancontact.",
  bancontactIconTitle: 'Bancontact icon',
  bancontactTitle: 'Bancontact',
  bancontactCardTitle: 'Bancontact Card',
  bancontactMobileTitle: 'Bancontact Mobile',
  creditCardNumber: 'Card Number',
  creditCardExpiry: 'Expiration Date',
  creditCardCvc: 'CVV',
  creditCardCvcTooltip: 'Enter the 3 or 4-digit code found on the front or back of your card.',
  creditCardIconTitle: 'Credit card icon',
  creditCardTitle: 'Credit Card',
  creditCardNumberErrorMessage: 'Enter a valid card number',
  creditCardExpiryErrorMessage: 'Enter a valid date',
  creditCardCvvErrorMessage: 'Enter a valid CVV',
  epsBank: 'Bank Name',
  epsRedirect: "To complete your payment, you'll be redirected to your bank.",
  epsIconTitle: 'EPS icon',
  epsTitle: 'EPS',
  idealBank: 'Bank Name',
  idealRedirect: "To complete your payment, you'll be redirected to your bank.",
  idealIconTitle: 'iDEAL icon',
  idealTitle: 'iDEAL',
  klarnaRedirect: "To complete your payment, you'll be redirected to Klarna.",
  klarnaIconTitle: 'Klarna icon',
  klarnaTitle: 'Klarna',
  klarnaPayLaterTitle: 'Klarna Pay Later',
  klarnaPaynowTitle: 'Klarna Pay Now',
  klarnaAccountTitle: 'Klarna Pay in Installments',
  paypalTitle: 'PayPal',
  paypalIconTitle: 'PayPal icon',
  paypalRedirect: "You'll be redirected to PayPal where you will complete your order.",
  venmoTitle: 'Venmo',
  venmoIconTitle: 'Venmo icon',
  venmoRedirect: "You'll be redirected to Venmo where you will complete your order.",
  twintTitle: 'TWINT',
  twintIconTitle: 'TWINT icon',
  twintRedirect: "To complete your payment, you'll be redirected to TWINT.",
  sepaDebitIconTitle: 'SEPA Debit icon',
  sepaDebitTitle: 'SEPA Debit',
  sepaDebitIban: 'IBAN',
  sepaDebitIbanErrorMessage: 'Enter a valid International Bank Account Number (IBAN)',
  sepaDebitAuthorizationMandate:
    'By providing your IBAN and confirming this payment, you are authorizing our company and Stripe, our payment service provider, to send instructions to your bank to debit your account in accordance with those instructions. You are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within eight weeks starting from the date on which your account was debited.',
  usBankAccount: '',
  usBankAccountIconTitle: 'ACH icon',
  usBankAccountTitle: 'ACH/Bank Account',
  usBankAccountPaymentMethodTitle: 'ACH/{0}',
  savePaymentMethodForFutureUse: 'Save payment method for future use',
  makePaymentMethodDefault: 'Make payment method default',
  savedPaymentMethodEndingWith: 'ending in {0}',
  savedPaymentMethodDefault: 'Default',
  savedPaymentMethodMerchantCreated: 'Merchant Saved',
  cardTypeIconTitle: '{0} icon',
  cardTypeVisa: 'Visa',
  cardTypeMastercard: 'MasterCard',
  cardTypeAmex: 'American Express',
  cardTypeDiners: 'Diners Club',
  cardTypeJcb: 'JCB',
  cardTypeDiscover: 'Discover',
  cardTypeUnionpay: 'UnionPay',
  viewAll: 'View all ({0} more)',
  redirectNotification: "To complete your payment, you'll be redirected.",
  paymentAppDescription: 'To complete your payment, follow the directions in the popup.',
  iconTitle: '{0} icon',
  finalAmountTitle: 'Final Amount',
  productAmountTitle: 'Product Amount',
  shippingAddressUnserviceable: 'Cannot ship to the selected address.',
  shippingOptionUnserviceable: 'Cannot ship via the selected option.',
};

export function label(labels: Labels, name: keyof Labels): string {
  return labels && labels[name] ? labels[name] : DEFAULT_LABELS[name];
}

export const PaymentMethodLabels: Map<string, string> = new Map([
  [PaymentMethodType.AFTERPAY_CLEARPAY, DEFAULT_LABELS.afterpayClearpayTitle],
  [PaymentMethodType.APPLEPAY, DEFAULT_LABELS.applePayTitle],
  [PaymentMethodType.AU_BECS_DEBIT, DEFAULT_LABELS.auBecsDebitTitle],
  [PaymentMethodType.BACS_DEBIT, DEFAULT_LABELS.bacsDebitTitle],
  [PaymentMethodType.BANCONTACT, DEFAULT_LABELS.bancontactTitle],
  [PaymentMethodType.CARD, DEFAULT_LABELS.creditCardTitle],
  [PaymentMethodType.EPS, DEFAULT_LABELS.epsTitle],
  [PaymentMethodType.GOOGLEPAY, DEFAULT_LABELS.googlePayTitle],
  [PaymentMethodType.IDEAL, DEFAULT_LABELS.idealTitle],
  [PaymentMethodType.KLARNA, DEFAULT_LABELS.klarnaTitle],
  [PaymentMethodType.PAYPAL, DEFAULT_LABELS.paypalTitle],
  [PaymentMethodType.TWINT, DEFAULT_LABELS.twintTitle],
  [PaymentMethodType.SEPA_DEBIT, DEFAULT_LABELS.sepaDebitTitle],
  [PaymentMethodType.US_BANK_ACCOUNT, DEFAULT_LABELS.usBankAccountTitle],
]);

export function getLabelsByCardType(network: string, labels: Labels): { label: string; iconTitle: string } {
  const cardType = network && network.toLowerCase();
  let cardTypeLabel;
  if (cardType === 'visa') {
    cardTypeLabel = label(labels, 'cardTypeVisa');
  } else if (cardType === 'mastercard') {
    cardTypeLabel = label(labels, 'cardTypeMastercard');
  } else if (cardType === 'amex') {
    cardTypeLabel = label(labels, 'cardTypeAmex');
  } else if (cardType === 'diners') {
    cardTypeLabel = label(labels, 'cardTypeDiners');
  } else if (cardType === 'jcb') {
    cardTypeLabel = label(labels, 'cardTypeJcb');
  } else if (cardType === 'discover') {
    cardTypeLabel = label(labels, 'cardTypeDiscover');
  } else if (cardType === 'unionpay') {
    cardTypeLabel = label(labels, 'cardTypeUnionpay');
  }
  if (cardTypeLabel) {
    return {
      label: cardTypeLabel,
      iconTitle: label(labels, 'cardTypeIconTitle').replace('{0}', cardTypeLabel),
    };
  }
  return {
    label: label(labels, 'creditCardTitle'),
    iconTitle: label(labels, 'creditCardIconTitle'),
  };
}

export const DEFAULT_THEME: Theme = {
  type: ThemeType.PLAIN,
  mode: ThemeMode.LIGHT,
  rules: {
    formLabel: {
      invalid: {},
    },
    input: {
      'line-height': '30px',
      focus: {},
      hover: {},
      invalid: {
        focus: {},
        hover: {},
      },
    },
    checkbox: {
      'background-color': '#fff',
      hover: {},
    },
    checkboxLabel: {
      'font-size': '0.76923rem',
      hover: {},
    },
    button: {
      focus: {},
      hover: {},
      active: {},
    },
    picklist: {},
    picklistOption: {
      hover: {},
    },
    error: {},
  },
};

const defaultDesignTokens: ThemeDesignTokens = {
  'font-family': '"Salesforce Sans",Helvetica,Arial,sans-serif',
  'font-size-2': '12px',
  'font-size-3': '13px',
  'font-size-4': '14px',
  'font-size-5': '16px',
  'font-size-6': '18px',
  'font-weight-regular': '400',
  'font-weight-bold': '700',
  'line-height-text': '24px',
  'color-text-default': '#181818',
  'color-text-error': '#ea001e',
  'color-text-placeholder': '#747474',
  'color-text-tooltip': '#fff',
  'color-text-weak': '#444',
  'color-background': '#fff',
  'color-brand': '#0176d3',
  'color-text-brand-primary': '#fff',
  'color-background-error': '#ba0517',
  'color-background-tooltip': '#032d60',
  'color-text-inverse': '#fff',
  'color-background-success': '#2e844a',
  'color-background-warning': '#fe9339',
  'color-text-icon-default': '#747474',
  'color-background-row-hover': '#f3f3f3',
  'color-border-input': '#c9c9c9',
  'palette-neutral-75': '#c1c1c2',
  'palette-neutral-95': '#f3f3f3',
  'border-radius-medium': '0.25rem',
  'border-radius-small': '0.125rem',
  'spacing-large': '1rem',
  'spacing-medium': '1rem',
  'spacing-small': '0.75rem',
  'spacing-x-small': '0.5rem',
  'spacing-xx-small': '0.25rem',
  'spacing-xxx-small': '0.125rem',
  'spacing-x-large': '2rem',
};

const darkDesignTokens: ThemeDesignTokens = {
  ...defaultDesignTokens,
  'color-text-default': '#fff',
  'color-background': '#000',
  'color-brand': '#1b96ff',
  'color-background-error': '#fe5c4c',
  'color-text-inverse': '#181818',
  'color-background-success': '#45c65a',
};

export const DEFAULT_THEME_DESIGN_TOKENS: { [key: string]: ThemeDesignTokens } = {
  'plain-dark': darkDesignTokens,
  'plain-light': defaultDesignTokens,
};

const STRIPE_CURRENCY_CODES_0_FRACTION_DIGITS = [
  'cve',
  'djf',
  'gnf',
  'idr',
  'jpy',
  'kmf',
  'krw',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
];
const STRIPE_CURRENCY_CODES_3_FRACTION_DIGITS = ['bhd', 'iqd', 'jod', 'kwd', 'lyd', 'omr', 'tnd'];

export function convertAmount(
  currency: string,
  amount: number,
  zeroFractionCurrencies: string[],
  threeFractionCurrencies: string[]
): number {
  const code = currency.toLowerCase();
  const compare = (v: string): boolean => code === v;
  let factor;
  if (zeroFractionCurrencies.find(compare)) {
    factor = 1;
  } else if (threeFractionCurrencies.find(compare)) {
    factor = 1000;
  } else {
    factor = 100;
  }
  return Math.trunc(Number(amount) * factor + 0.1);
}

export function stripeAmount(currency: string, amount: number): number {
  return convertAmount(
    currency,
    amount,
    STRIPE_CURRENCY_CODES_0_FRACTION_DIGITS,
    STRIPE_CURRENCY_CODES_3_FRACTION_DIGITS
  );
}

const ADYEN_CURRENCY_CODES_0_FRACTION_DIGITS = [
  'cve',
  'djf',
  'gnf',
  'idr',
  'jpy',
  'kmf',
  'krw',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
];
const ADYEN_CURRENCY_CODES_3_FRACTION_DIGITS = ['bhd', 'iqd', 'jod', 'kwd', 'lyd', 'omr', 'tnd'];

export function adyenAmount(currency: string, amount: number): number {
  return convertAmount(
    currency,
    amount,
    ADYEN_CURRENCY_CODES_0_FRACTION_DIGITS,
    ADYEN_CURRENCY_CODES_3_FRACTION_DIGITS
  );
}

export function defer<T>(timeoutMillis?: number, timeoutValue?: T): Deferred<T> {
  let resolve: (value: T | PromiseLike<T>) => void;
  let reject: (reason?: any) => void;
  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  if (timeoutMillis) {
    setTimeout(() => resolve(timeoutValue), timeoutMillis);
  }

  return {
    promise,
    resolve,
    reject,
  };
}

export function doWithTimeout<T>(timeoutMillis: number, workFn: Promise<T>): Promise<T> {
  let timeout: any;
  const timeoutPromise: Promise<T> = new Promise((resolve, reject) => {
    timeout = setTimeout(() => reject('timeout'), timeoutMillis);
  });
  return Promise.race([timeoutPromise, workFn]).finally(() => clearTimeout(timeout));
}
